<template>
  <b-dropdown
    :triggers="['hover']"
    aria-role="list"
    position="is-bottom-left"
    style="width: 120px"
  >
    <template #trigger>
      <b-button
        :type="{
          'is-success': t.details.status === 'success',
          'is-smart-orange-7': ['refused', 'preemptivelyRefused'].includes(
            t.details.status
          ),
          'is-light': t.details.status === 'cancelled',
          'is-warning': t.details.status === 'error',
          'is-info': t.details.status === 'partial',
          'is-dark':
            t.details.status === 'refunded' || t.details.status === 'expired',
          'is-danger': t.details.status === 'chargeback',
          'is-sea': t.details.status === 'pending',
          'is-light-green':
            t.details.status === 'billingPending' ||
            t.details.status === 'chargebackPaid'
        }"
        :disabled="t.details.status === 'cancelled'"
        slot="trigger"
      >
        {{
          t.details.status === "success"
            ? $t("transactions.statusSuccess")
            : ["refused", "preemptivelyRefused"].includes(t.details.status)
            ? $t("transactions.statusRefused")
            : t.details.status === "error"
            ? $t("transactions.statusError")
            : t.details.status === "cancelled"
            ? $t("transactions.statusCancelled")
            : t.details.status === "open"
            ? $t("transactions.statusOpen")
            : t.details.status === "refundedRequested"
            ? $t("transactions.statusRefundRequested")
            : t.details.status === "refunded"
            ? $t("transactions.statusRefunded")
            : t.details.status === "refundRefused"
            ? $t("transactions.statusRefundRefused")
            : t.details.status === "refundFailed"
            ? $t("transactions.statusRefundFailed")
            : t.details.status === "refundReversed"
            ? $t("transactions.statusRefundReversed")
            : t.details.status === "chargeback"
            ? $t("transactions.statusChargeback")
            : t.details.status === "pending"
            ? $t("transactions.statusPending")
            : t.details.status === "partial"
            ? $t("transactions.statusPartial")
            : t.details.status === "billingPending"
            ? $t("transactions.statusBillingPending")
            : t.details.status === "expired"
            ? $t("transactions.expired")
            : t.details.status === "chargebackPaid"
            ? $t("transactions.statusChargebackPaid")
            : "-"
        }}
      </b-button>
    </template>

    <div v-if="t.details.status !== 'cancelled'">
      <b-dropdown-item aria-role="listitem">
        <p>
          {{ paymentMethods }}
          {{
            t.details.format === "ecommerce"
              ? `${
                  t.user &&
                  t.user.accountInfo &&
                  t.user.accountInfo.isTotemAccount
                    ? ""
                    : " (App)"
                }`
              : t.details.format === "terminal"
              ? " (Terminal)"
              : t.details.format === "googlepay"
              ? " (Google Pay)"
              : "?"
          }}
        </p>
      </b-dropdown-item>

      <hr class="separator has-background-primary" />

      <b-dropdown-item aria-role="listitem">
        <b>{{ $t("transactions.cart") }}: </b>
        {{
          `${getCurrencySymbolBasedOnCurrency(t.details.currency)}${formatMoney(
            t.cart.totalValue
          )}`
        }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-if="t.details.status === 'pending'"
        @click="copyQRCodeData"
      >
        <button class="button is-warning">
          {{ $t("transactions.copyQrCode") }}
        </button>
      </b-dropdown-item>

      <div
        v-if="
          (t.discounts.coupon && t.discounts.coupon.total) ||
            t.discounts.cashbackUsed
        "
      >
        <b-dropdown-item
          aria-role="listitem"
          v-if="t.discounts.coupon && t.discounts.coupon.total"
        >
          <b>(-) {{ $t("transactions.coupon") }}: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.discounts.coupon.total)}`
          }}
          <br />
          <small>
            {{
              t.discounts.coupon.type === "absolute"
                ? `--> ${
                    t.discounts.coupon.name
                  } ${getCurrencySymbolBasedOnCurrency(
                    t.details.currency
                  )}${formatMoney(t.discounts.coupon.value)}`
                : t.discounts.coupon.type === "percentual"
                ? `--> ${t.discounts.coupon.name} ${t.discounts.coupon.value}%`
                : "?"
            }}
          </small>
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem" v-if="t.discounts.cashbackUsed">
          <b>(-) Cashback: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.discounts.cashbackUsed)}`
          }}
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <hr class="bill-separator has-background-elegant-black-3" />
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <b>Total a pagar: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.details.chargedValue)}`
          }}
        </b-dropdown-item>
      </div>

      <b-dropdown-item aria-role="listitem" v-if="t.details.prepaid">
        <b>Créditos: </b>
        {{
          `${getCurrencySymbolBasedOnCurrency(t.details.currency)}${formatMoney(
            t.details.prepaid
          )}`
        }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-if="
          t.details.status === 'refused' && t.details.format === 'ecommerce'
        "
      >
        <b>Retorno: </b>
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-if="t.details.status === 'preemptivelyRefused'"
      >
        <b>Retorno: </b>
        {{
          `${t.card.irreversibleInfo.code}: ${t.card.irreversibleInfo.message}`
        }}
      </b-dropdown-item>

      <div v-if="t.rewards.cashbackAccumulated.total">
        <b-dropdown-item aria-role="listitem">
          <hr class="bill-separator has-background-elegant-black-3" />
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <b>(+) Cashback: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.rewards.cashbackAccumulated.total)} (${
              t.rewards.cashbackAccumulated.percentage
            }%)`
          }}
        </b-dropdown-item>
      </div>
    </div>
  </b-dropdown>
</template>

<script>
import { mask } from "../../../mixins/mask";
import { common } from "../../../mixins/common";

export default {
  mixins: [mask, common],

  props: ["t"],

  computed: {
    paymentMethods() {
      let p = [];
      if (this.t.discounts.coupon && this.t.discounts.coupon.total) {
        p.push(this.$t("transactions.coupon"));
      }

      if (this.t.discounts.cashbackUsed) {
        p.push(this.$t("transactions.cashback"));
      }

      if (this.t.details.prepaid) {
        p.push(this.$t("transactions.prepaid"));
      }

      if (this.t.details.fundingSource === "pix") {
        p.push(this.$t("transactions.pix"));
      }
      return p.join(" + ");
    }
  },

  methods: {
    copyQRCodeData() {
      const obj = this.t;
      if (obj && obj.pagbank && obj.pagbank.response) {
        if (
          obj.pagbank.response.qr_codes &&
          obj.pagbank.response.qr_codes.length > 0
        ) {
          try {
            navigator.clipboard.writeText(
              obj.pagbank.response.qr_codes[0].text
            );
            this.$buefy.toast.open({
              message: this.$t("transactions.QrCodeHasBeenCopied"),
              type: "is-primary"
            });
            return;
          } catch (err) {}
        }
      }

      this.alertError({ message: "COPY_FAILED" });
    }
  }
};
</script>

<style scoped>
.separator {
  height: 1px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.bill-separator {
  height: 1px;
  margin-top: 0.1rem;
  margin-bottom: 0.1rem;
}
</style>
